import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Divider, Collapse } from '@mui/material';
import MexicoLatAmMap from '../shared/components/Mapa/Mexico';
import PageShell from '../shared/components/PageShell';
import { BacteriumSelect } from '../shared/components/BacteriumSelect';
import EscapeArtistsChart from '../shared/components/EscapeArtistsChart';
import CollectionDatesChart from '../shared/components/CollectionDatesChart';
import PieChart from '../shared/components/PieChart';
import { CountrySelectLatAm } from '../shared/components/CountrySelectLatAm';
import { apiRequest } from '../shared/api/apiClient';


export default function BrowseV2() {
    const [selectedCountry, setSelectedCountry] = useState("All countries");
    const [selectedId, setSelectedId] = useState(null);
    const [bacteriaSelected, setBacteriaSelected] = useState("Select All")
    const [dataMap, setDataMap] = useState({});
    const [dataPie, setDataPie] = useState([]);
    const [dataBarHI, setDataBarHI] = useState([]);
    const [dataCollection, setDataCollection] = useState([]);
    const [dataBarByGene, setDataBarByGene] = useState([]);
    const [dataBarByAnti, setDataBarByAnti] = useState([]);
    const [dataBarVirulence, setDataBarVirulence] = useState([]);
    const [totalIsolates, setTotalIsolates] = useState(0);
    const [openData, setOpenData] = useState(false);
    const [emptyData, setEmptyData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fecha, setFecha] = useState('')

    const [showMap, setShowMap] = useState(true);

    React.useEffect(() => {
        setShowMap(false);
        const t = setTimeout(() => setShowMap(true), 50);
        return () => clearTimeout(t);
    }, [bacteriaSelected]);

    const fetchChartData = (organism, country) => {
        const token = "569d48e99cc70b43a136acd5cf710ffdf3674d806f5dc129163290c3925373c2";
        const org = organism === "Select All" ? "all" : organism;
        const ctry = (country === "All countries") ? "all" : country;
        const url = `http://api-resistencia.inmegen.gob.mx/chart_dash.php?token=${token}&organism=${encodeURIComponent(org)}&country=${encodeURIComponent(ctry)}`;
        return apiRequest(url, { method: "POST" });
    };

    function sumValues(obj) {
        return Object.values(obj).reduce((sum, value) => sum + value, 0);
    }

    function getUpdatedDate() {
        const date = new Date();

        const day = date.getDate();
        const year = date.getFullYear();

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        const month = months[date.getMonth()];

        function getOrdinal(n) {
            if (n > 3 && n < 21) return "th";
            switch (n % 10) {
                case 1: return "st";
                case 2: return "nd";
                case 3: return "rd";
                default: return "th";
            }
        }

        return `Updated: ${day}${getOrdinal(day)} ${month} ${year}`;
    }

    useEffect(() => {

        let cancelled = false;

        const updateData = async () => {
            setLoading(true);
            try {
                const data = await fetchChartData(bacteriaSelected, selectedCountry);
                if (!cancelled) {
                    setDataMap(data[bacteriaSelected].mapa);
                    setDataPie(data[bacteriaSelected]["Hosts and isolation source"]["pieChart"]);
                    setDataBarHI(data[bacteriaSelected]["Hosts and isolation source"]["barChart"]);
                    setDataCollection(data[bacteriaSelected]["Collection dates"]);
                    setDataBarByGene(data[bacteriaSelected]["Resistance mechanisms"]["byGene"]);
                    setDataBarByAnti(data[bacteriaSelected]["Resistance mechanisms"]["byAntimicrobialClass"]);
                    setDataBarVirulence(data[bacteriaSelected]["Virulence factor"]);
                    setTotalIsolates(sumValues(data[bacteriaSelected].mapa));
                    setFecha(getUpdatedDate());
                    setLoading(false);
                }
                if (sumValues(data[bacteriaSelected].mapa) > 0) {
                    setEmptyData(false);
                    setOpenData(true);
                } else {
                    setOpenData(false);
                    setEmptyData(true);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error(e);
                    setLoading(false);
                }
            }
        };

        updateData();

        return () => {
            cancelled = true;
        };
    }, [bacteriaSelected, selectedCountry]);



    const genePrefixes = ["bla", "mec", "tet", "aac", "erm", "van", "qnr", "sul"];
    const EAdata = Array.from({ length: 200 }, (_, i) => {
        const prefix = genePrefixes[i % genePrefixes.length];
        return {
            name: `${prefix}${(i + 1).toString().padStart(3, "0")}`,
            value: Math.floor(Math.random() * 120) + 5
        };
    });
    return (
        <Box>
            <PageShell>
                <Grid
                    container
                    spacing={4}
                    sx={{ width: '100%' }}
                >
                    <Grid item size={{ xs: 12, md: 12, lg: 6, xl: 6 }}>
                        <Box sx={{
                            width: '100%',
                            pt: { xs: 0, lg: '65px' },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', lg: 'flex-start' }
                        }}>
                            <Box sx={{ width: '100%', maxWidth: '705px', mb: 10, textAlign: { xs: 'center', lg: 'left' } }}>
                                <Typography variant='h4'>
                                    Data per Region
                                </Typography>
                            </Box>

                            <Box sx={{ width: '100%', maxWidth: '415px', mb: '25px', textAlign: { xs: 'center', lg: 'left' } }}>
                                <Typography variant='h5'>
                                    Interactive map
                                </Typography>
                            </Box>

                            <Box sx={{ width: '100%', maxWidth: '560px', mb: '60px', textAlign: { xs: 'center', lg: 'left' } }}>
                                <Typography variant='body1'>
                                    This page displays the number of isolates recorded in the database for each country in the region.
                                    Once you have selected a bacterium, choose a country to view the full information for that country,
                                    including resistance mechanisms, hosts, isolation source and collection dates.
                                </Typography>
                            </Box>

                            <BacteriumSelect
                                options={opts}
                                value={bacteriaSelected}
                                onChange={setBacteriaSelected}
                                sx={{ maxWidth: 560, mb: 2, width: '100%' }}
                            />
                            <CountrySelectLatAm
                                value={selectedCountry}
                                onChange={(name) => {
                                    setSelectedCountry(name);

                                    if (name === "All countries") {
                                        setSelectedId(null);
                                        return;
                                    }

                                    setSelectedId(NAME_TO_ISO[name] ?? null);
                                }}
                                sx={{ maxWidth: 560, mb: 2, width: '100%' }}
                            />

                        </Box>
                    </Grid>

                    <Grid item size={{ xs: 12, md: 12, lg: 6, xl: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: 600,
                                border: '1px solid #000',
                                borderRadius: 3,
                                overflow: 'hidden',
                                mb: 4,
                                maxWidth: "680px"
                            }}
                        >
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    position: 'relative',
                                    bgcolor: '#fff',
                                    '& .leaflet-container': {
                                        position: 'absolute',
                                        inset: 0,
                                    },
                                }}
                            >
                                <MexicoLatAmMap
                                    dataKey={bacteriaSelected}
                                    countriesData={dataMap}
                                    selectedId={selectedId}
                                    setSelectedId={setSelectedId}
                                    onSelectCountry={(iso, name) => {
                                        setSelectedId(iso);
                                        setSelectedCountry(name);
                                    }}
                                    isolates={totalIsolates}
                                    lastUpdated={fecha}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </PageShell>

            <PageShell>
                <Divider sx={{ borderColor: "#20BC71" }} />
            </PageShell>

            <Collapse sx={{ width: '100%' }} in={openData}>
                <Collapse sx={{ width: '100%' }} in={dataPie.length > 0}>
                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid item size={6}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, alignContent: 'flex-end' }}>
                                    <Box sx={{ width: '100%', maxWidth: '705px', textAlign: 'left' }}>
                                        <Typography variant='h5'>
                                            Hosts and isolation source
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item size={6}>
                                <Grid sx={{ width: "100%" }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                                        <Box sx={{ width: '100%', textAlign: "end" }}>
                                            <Typography variant='h5'>
                                                {bacteriaSelected === "Select All"
                                                    ? "All Bacteria Statistics"
                                                    : `${bacteriaSelected} Statistics`}
                                            </Typography>
                                            <Typography variant='h6'>
                                                {selectedCountry}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </PageShell>

                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid size={{ xs: 12, md: 12, lg: 6, xl: 6 }}>
                                <Box>
                                    <PieChart data={dataPie} isolates={50} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12, lg: 6, xl: 6 }}>
                                <Box>
                                    <EscapeArtistsChart data={dataBarHI} isolates={totalIsolates} tittle={"Isolation source"} activeTitle={true} />
                                </Box>
                            </Grid>
                        </Grid>
                    </PageShell>

                    <PageShell>
                        <Divider sx={{ borderColor: "#20BC71" }} />
                    </PageShell>
                </Collapse>

                <Collapse sx={{ width: "100%" }} in={dataCollection.length > 0}>
                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid item size={6}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, alignContent: 'flex-end' }}>
                                    <Box sx={{ width: '100%', maxWidth: '705px', textAlign: 'left' }}>
                                        <Typography variant='h5'>
                                            Collection dates
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item size={6}>
                                <Grid sx={{ width: "100%" }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                                        <Box sx={{ width: '100%', textAlign: "end" }}>
                                            <Typography variant='h5'>
                                                {bacteriaSelected === "Select All"
                                                    ? "All Bacteria Statistics"
                                                    : `${bacteriaSelected} Statistics`}
                                            </Typography>
                                            <Typography variant='h6'>
                                                {selectedCountry}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </PageShell>
                    <PageShell>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', lg: 'flex-start' } }}>
                            <CollectionDatesChart data={dataCollection} isolates={45} />
                        </Box>
                    </PageShell>

                    <PageShell>
                        <Divider sx={{ borderColor: "#20BC71" }} />
                    </PageShell>
                </Collapse>

                <Collapse sx={{ width: '100%' }} in={dataBarByGene.length > 0}>
                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid item size={6}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, alignContent: 'flex-end' }}>
                                    <Box sx={{ width: '100%', maxWidth: '705px', textAlign: 'left' }}>
                                        <Typography variant='h5'>
                                            Resistance mechanisms
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item size={6}>
                                <Grid sx={{ width: "100%" }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                                        <Box sx={{ width: '100%', textAlign: "end" }}>
                                            <Typography variant='h5'>
                                                {bacteriaSelected === "Select All"
                                                    ? "All Bacteria Statistics"
                                                    : `${bacteriaSelected} Statistics`}
                                            </Typography>
                                            <Typography variant='h6'>
                                                {selectedCountry}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </PageShell>
                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid size={{ xs: 12, md: 12, lg: 6, xl: 6 }}>
                                <Box >
                                    <EscapeArtistsChart
                                        data={dataBarByGene}
                                        isolates={totalIsolates}
                                        tittle={"By Gene"}
                                        activeTitle={true}
                                        customIsolates={{ active: true, value: totalIsolates }}
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12, lg: 6, xl: 6 }}>
                                <Box >
                                    <EscapeArtistsChart
                                        data={dataBarByAnti}
                                        isolates={totalIsolates}
                                        tittle={"By Antimicrobial Class"}
                                        customIsolates={{ active: true, value: totalIsolates }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </PageShell>

                    <PageShell>
                        <Divider sx={{ borderColor: "#20BC71" }} />
                    </PageShell>
                </Collapse>


                <Collapse in={dataBarVirulence.length > 0} sx={{ width: '100%' }}>
                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid item size={6}>
                                <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, alignContent: 'flex-end' }}>
                                    <Box sx={{ width: '100%', maxWidth: '705px', textAlign: 'left' }}>
                                        <Typography variant='h5'>
                                            Virulence factor
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item size={6}>
                                <Grid sx={{ width: "100%" }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                                        <Box sx={{ width: '100%', textAlign: "end" }}>
                                            <Typography variant='h5'>
                                                {bacteriaSelected === "Select All"
                                                    ? "All Bacteria Statistics"
                                                    : `${bacteriaSelected} Statistics`}
                                            </Typography>
                                            <Typography variant='h6'>
                                                {selectedCountry}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </PageShell>

                    <PageShell>
                        <Grid container spacing={2} sx={{ width: "100%" }}>
                            <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                                <Box sx={{ mb: '100px' }}>
                                    <EscapeArtistsChart
                                        data={dataBarVirulence}
                                        isolates={totalIsolates}
                                        activeTitle={true}
                                        tittle={"Virulence factor"}
                                        customIsolates={{ active: true, value: totalIsolates }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </PageShell>
                </Collapse>
            </Collapse>

            <Collapse sx={{ width: '100%' }} in={emptyData}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
                    <Typography variant='h5'>
                        No data available for the selected filters
                    </Typography>
                    <Typography variant='body1'>
                        There are currently no isolates recorded for this bacterium and country combination
                    </Typography>
                </Box>
            </Collapse>



        </Box>
    )
}



const opts = [
    "Acinetobacter baumannii",
    "Klebsiella pneumoniae",
    "Staphylococcus aureus",
    "Select All",
];


const NAME_TO_ISO = {
    "All countries": "All cuntries",
    Argentina: "ARG",
    Belize: "BLZ",
    Bolivia: "BOL",
    Brazil: "BRA",
    Chile: "CHL",
    Colombia: "COL",
    "Costa Rica": "CRI",
    Cuba: "CUB",
    "Dominican Republic": "DOM",
    Ecuador: "ECU",
    "El Salvador": "SLV",
    Guatemala: "GTM",
    Haiti: "HTI",
    Honduras: "HND",
    Jamaica: "JAM",
    Mexico: "MEX",
    Nicaragua: "NIC",
    Panama: "PAN",
    Paraguay: "PRY",
    Peru: "PER",
    "Trinidad and Tobago": "TTO",
    Uruguay: "URY",
    Venezuela: "VEN",
};




